// src/styles/theme.js
import { createTheme } from '@mui/material/styles';
//import tinycolor from 'tinycolor2';

// Define colors or fetch CSS variables if needed
/*const toolbarButtonColor = getComputedStyle(document.documentElement).getPropertyValue('--toolbar-button-color').trim();
const brightenedColor = tinycolor(toolbarButtonColor).lighten(20).toString();*/

const theme = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '999px', // Semi-circle rounded corners
          padding: '10px 20px',  // Adjust padding for desired size
          fontSize: 'var(--font-button-size)',
          backgroundColor: 'var(--btn-primary-bg)', // Use the CSS variable for background color
          color: '#fff', // Text color (white)
          margin: '10px',
          '&:hover': {
            backgroundColor: 'var(--btn-primary-hover-bg)', // Background color on hover
          },
        },
        contained: {
          boxShadow: 'none', // Optional: Remove shadow for contained buttons
        },
      },
      variants: [
        {
          props: { variant: 'toolbar' }, // Custom variant for toolbar buttons
          style: {
            fontFamily: 'var(--font-primary)',
            backgroundColor: 'var(--toolbar-button-color)', // Custom background color for toolbar buttons
            color: 'var(--text-primary)', // Text color
            padding: '5px', // Custom padding for toolbar buttons
            fontSize: 'var(--font-button-size)',
            textTransform: 'none', // Prevents automatic uppercase text
            borderRadius: '0px',
            '&:hover': {
              backgroundColor: 'var(--toolbar-button-color)', // Slightly brighter on hover
            },
          },
        },
        {
          props: { variant: 'notificationAction' },
          style: {
            //opacity: '70%',  // Subtle text color, like a link
            backgroundColor: 'transparent',  // No background
            boxShadow: 'none',  // Remove box shadow
            padding: '5px',  // Remove padding to make it more like a link
            '&:hover': {
              backgroundColor: 'transparent',  // Keep no background on hover
            },
          },
        },
      ],
    },
    MuiFormControl: {
      variants: [
        {
          props: { variant: 'toolbar' }, // Custom variant for toolbar FormControl
          style: {
            backgroundColor: 'var(--toolbar-button-color)', // Match the toolbar button color
            borderRadius: '25px', // Rounded corners for consistency
            padding: '10px', // Consistent padding with toolbar buttons
            minWidth: '150px', // Ensure a minimum width
            fontSize: 'var(--font-button-size)',
            '& .MuiOutlinedInput-root': {
              color: '#fff', // Text color
              borderColor: 'transparent', // No border by default
              '& fieldset': {
                borderColor: 'transparent', // Remove border from fieldset
              },
              '&:hover fieldset': {
                borderColor: 'transparent', // No border on hover
              },
              '&.Mui-focused fieldset': {
                borderColor: 'transparent', // No border when focused
              },
            },
          },
        },
      ],
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-primary)',
          fontWeight: 'var(--font-weight-normal)',
        },
        paper: {
          backgroundColor: 'var(--toolbar-menu-background-color)',
          borderRadius: '20px', // Adjust border radius
          boxShadow: 'var(--shadow-sm)', // Match the toolbar shadow
          padding: '10px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: 'var(--text-light-primary)', // Ensure text color matches the toolbar
          fontFamily: 'var(--font-primary)',
          fontWeight: 'var(--font-weight-normal)',
          '&:hover': {
            backgroundColor: 'var(--toolbar-button-color)', 
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px', // Set the borderRadius to make all IconButtons rectangular
          padding: '8px', // Optional: adjust padding to ensure it looks good with the rectangular shape
        },
      },
    },
    variants: [
      {
        props: { variant: 'wordlist' },
        style: {
          backgroundColor: 'var(--toolbar-icon-color)',
          border: 0,
          fontSize: '14px',
          '&:hover': {
            backgroundColor: 'var(--toolbar-icon-color)', // gray
          },
        },
      },
      {
        props: { variant: 'toolbar' },
        style: {
          backgroundColor: 'var(--toolbar-button-color)',
          '&:hover': {
            backgroundColor: 'var(--toolbar-button-color)', // Keep the same hover effect for toolbar buttons
          },
        }
      },
    ],
    MuiTextField: {
      defaultProps: {
        inputProps: {
          maxLength: 50, // Set the maximum length here
        },
      },
      styleOverrides: {
        root: {
          width: '100%', // Set width to 100% by default
          '& .MuiInputBase-root': {
            borderRadius: '999px', // Semi-circle rounded corners
            backgroundColor: 'var(--input-background-color)',
            fontFamily: 'var(--font-primary)', // Apply custom font to input
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '999px', // Semi-circle rounded corners for outlined variant
            backgroundColor: 'var(--input-background-color)',
            fontFamily: 'var(--font-primary)', // Apply custom font to input
          },
          '& .MuiFilledInput-root': {
            borderRadius: '999px', // Semi-circle rounded corners for filled variant
            backgroundColor: 'var(--input-background-color)',
            fontFamily: 'var(--font-primary)', // Apply custom font to input
          },
          '& .MuiInput-root': {
            borderRadius: '999px', // Semi-circle rounded corners for standard variant
            backgroundColor: 'var(--input-background-color)',
            fontFamily: 'var(--font-primary)', // Apply custom font to input
          },
          '& .MuiInputLabel-root': {
            fontFamily: 'var(--font-primary)', // Apply custom font to label
          },
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: '32px 0',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: '1.1rem',
          fontWeight: 400,
          fontFamily: 'var(--font-primary)',
          color: 'var(--stepper-color)', 
          fontWeight: 700,
          '&.Mui-active': {
            fontWeight: 700,
            color: 'var(--stepper-active-color)', 
          },
          '&.Mui-completed': {
            opacity: 1,
            color: 'var(--stepper-completed-color)',
            fontWeight: 700,
          },
        },
        iconContainer: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          fontSize: '32px', 
          color: 'var(--stepper-color)', 
          '&.Mui-active': {
            color: 'var(--stepper-active-color)', 
            fontWeight: 700,
          },
          '&.Mui-completed': {
            color: 'var(--stepper-completed-color)',
          },
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: 'var(--stepper-color)',
          borderWidth: '5px',
          borderRadius: '5px',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '50px',
          backgroundColor: 'var(--tekhelet)',
          color: 'var(--bright-yellow)',
          fontWeight: 700,
          marginBottom: '16px', // ✅ Add space between items
          overflow: 'hidden',   // ✅ Ensures full border-radius applies
          '&.Mui-expanded': {
            backgroundColor: 'var(--bright-yellow)', // 🔸 Add this color
            color: 'var(--tekhelet)', // Optional: change text to dark for contrast
          },
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: '50px !important',
          overflow: 'hidden',
          paddingLeft: '16px',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          borderBottomLeftRadius: '50px',
          borderBottomRightRadius: '50px',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: '#fff',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#fff',
          '&.Mui-checked': {
            color: '#fff',
          },
        },
      },
    },
  }
});

export default theme;
